import React, {useRef} from "react";
import {
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import {FiCheck, FiChevronDown, FiFolder} from "react-icons/fi";
import useUserStore, {type IUserState} from "../../store/useUserStore.ts";
import useSystemStore from "../../store/useSystemStore.ts";
import type {
  NewProjectConfiguration,
  NewProjectConfigurations,
} from "../../models/interfaces/newProjectConfigurations.ts";
import {sanitizePreset, type SanitizedPreset} from "../../utils/presetSanitizer.ts";
import type {SubtitleStep} from "./LanguagesStep.tsx";

interface OpenTemplateDropdownProps {
  customConfigurationId?: string;
  selectedSourceLanguage: string;
  setCustomConfiguration: (configId: string | undefined) => void;
  applyPreset: (preset: SanitizedPreset) => void;
  goToSubtitleStep: (step: SubtitleStep) => void;
}

const EMPTY_CONFIGURATIONS: NewProjectConfigurations = {};

export const OpenTemplateDropdown: React.FC<OpenTemplateDropdownProps> = ({
  customConfigurationId,
  selectedSourceLanguage,
  setCustomConfiguration,
  applyPreset,
  goToSubtitleStep,
}: OpenTemplateDropdownProps) => {
  const savedConfigurations: NewProjectConfigurations | undefined = useUserStore(
    (state: IUserState) => state.preferences?.newProjectCreation,
  );
  const configurations: NewProjectConfigurations = savedConfigurations ?? EMPTY_CONFIGURATIONS;
  const supportedCodeLanguages: string[] = useSystemStore(
    (state) => state.supportedCodeLanguages,
  );
  const {isOpen, onOpen, onClose} = useDisclosure();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const templates: NewProjectConfiguration[] = Object.values(configurations);
  const selectedTemplate: NewProjectConfiguration | undefined = customConfigurationId
    ? configurations[customConfigurationId]
    : undefined;

  const selectTemplate = (template: NewProjectConfiguration): void => {
    if (template.id === customConfigurationId) {
      setCustomConfiguration(undefined);
      onClose();
      triggerRef.current?.focus();
      return;
    }

    setCustomConfiguration(template.id);
    if (template.config) {
      const preset: SanitizedPreset = sanitizePreset(template, supportedCodeLanguages);
      applyPreset(preset);
      const hasSourceConflict: boolean = preset.sourceLanguageName === null
        || (selectedSourceLanguage !== "" && selectedSourceLanguage !== preset.sourceLanguageName);
      goToSubtitleStep(hasSourceConflict || preset.isTranscribeOnly ? "source" : "targets");
    }
    onClose();
    triggerRef.current?.focus();
  };

  return (
    <div className="w-full text-left">
      <p className="mb-2 base2 font-semibold">Template</p>
      <Menu
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom-start"
        matchWidth
        closeOnBlur
        closeOnSelect={false}
        gutter={4}
      >
        <MenuButton
          ref={triggerRef}
          as="button"
          type="button"
          aria-label="Open Template"
          className="flex min-h-[3.25rem] w-full items-center gap-3 rounded-xl border border-n-500 px-4 text-left text-sm font-semibold text-n-100 transition-[background-color,transform] duration-[200ms,120ms] ease-[linear,cubic-bezier(0.2,0,0,1)] hover:bg-n-600 active:scale-[0.96] motion-reduce:transition-none"
        >
          <span className="flex w-full items-center gap-3">
            <FiFolder aria-hidden="true" className="shrink-0 text-n-300" size={20}/>
            <span className="min-w-0 flex-1">
              <span className="block">Open Template</span>
              {selectedTemplate && (
                <span aria-hidden="true" className="block truncate text-xs font-normal text-n-400">
                  {selectedTemplate.name}
                </span>
              )}
            </span>
            <FiChevronDown
              aria-hidden="true"
              className={`shrink-0 text-n-300 transition-transform duration-200 ease-linear motion-reduce:transition-none ${isOpen ? "rotate-180" : "rotate-0"}`}
              size={18}
            />
          </span>
        </MenuButton>
        <Portal>
          <MenuList
            data-testid="template-dropdown"
            data-template-surface="dropdown"
            aria-label="Open Template"
            bg="n.600"
            borderColor="n.500"
            borderRadius="xl"
            boxShadow="xl"
            minW="unset"
            w="100%"
            maxH="15rem"
            overflowY="auto"
            p="1"
          >
            {templates.length === 0 ? (
              <p className="text-sm text-n-400 text-pretty">No saved templates yet.</p>
            ) : (
              <MenuOptionGroup
                type="radio"
                value={customConfigurationId ?? ""}
                className="flex flex-col gap-2"
              >
                {templates.map((template: NewProjectConfiguration) => {
                  const isSelected: boolean = template.id === customConfigurationId;
                  return (
                    <MenuItemOption
                      key={template.id}
                      value={template.id}
                      icon={<FiCheck aria-hidden="true"/>}
                      iconPlacement="end"
                      iconSpacing="0"
                      onClick={() => selectTemplate(template)}
                      bg={isSelected ? "brandPurple.500" : "transparent"}
                      color={isSelected ? "n.100" : "n.300"}
                      minH="2.5rem"
                      borderRadius="lg"
                      px="3"
                      py="2"
                      gap="2"
                      fontSize="sm"
                      fontWeight="semibold"
                      sx={{
                        transitionProperty: "background-color, transform",
                        transitionDuration: "200ms, 120ms",
                        transitionTimingFunction: "linear, cubic-bezier(0.2, 0, 0, 1)",
                        "@media (prefers-reduced-motion: reduce)": {
                          transitionProperty: "none",
                        },
                      }}
                      _hover={{bg: isSelected ? "brandPurple.400" : "n.500"}}
                      _focus={{bg: isSelected ? "brandPurple.400" : "n.500"}}
                      _focusVisible={{
                        bg: isSelected ? "brandPurple.400" : "n.500",
                        outline: "2px solid",
                        outlineColor: "n.100",
                        outlineOffset: "-2px",
                      }}
                      _active={{transform: "scale(0.96)"}}
                    >
                      <span className="min-w-0 flex-1 truncate font-semibold">{template.name}</span>
                    </MenuItemOption>
                  );
                })}
              </MenuOptionGroup>
            )}
          </MenuList>
        </Portal>
      </Menu>
    </div>
  );
};
